import React, { useState, useEffect, useCallback } from 'react';
import { View, Dimensions, ScrollView, Linking } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import moment from 'moment';
import HtmlRenderer from 'react-native-render-html';

import { Heading, Dialog, Button } from '../../components';
import { useTheme } from '../../theme';
import useAcknowledgeServiceRunBrief from './useAcknowledgeServiceRunBrief';

const ServiceRunBrief = ({
	serviceRun,
	style,
	secondary = true,
	hideButton = false,
	isOpen: controlledIsOpen,
	setIsOpen: controlledSetIsOpen,
	requireAcknowledgement = false,
	onAcknowledged,
	disablePermanentDismiss = false,
	disableActions = false,
	adminTraining = false,
}) => {
	const { theme } = useTheme();
	const [internalIsOpen, setInternalIsOpen] = useState(false);
	const [isPermanentlyDismissed, setIsPermanentlyDismissed] = useState(false);
	const [acknowledgeBriefMutation, { loading: acknowledging }] = useAcknowledgeServiceRunBrief({
		serviceRunId: serviceRun.id,
		adminTraining,
	});

	// Controlled mode lets the My day board open the dialog from a step row;
	// existing call sites pass nothing and keep the internal state.
	const isControlled = typeof controlledSetIsOpen === 'function';
	const isBriefOpen = isControlled ? Boolean(controlledIsOpen) : internalIsOpen;
	const setIsBriefOpen = isControlled ? controlledSetIsOpen : setInternalIsOpen;

	const isServiceRunInPast = moment().isAfter(moment(serviceRun.planned), 'day');

	const {
		id,
		brief,
	} = serviceRun;

	// Show service run brief if exists. Show it only once if the service run is today or in the past.
	useEffect(() => {
		if (isServiceRunInPast || !brief) {
			// No brief to show
		}

		else if (!isControlled) {
			(async () => {
				const isDismissed = await AsyncStorage.getItem(`servicerun_${id}_brief_dismissed`);

				if (!isDismissed) {
					setInternalIsOpen(true);
				}
				else {
					setIsPermanentlyDismissed(true);
				}
			})();
		}
	}, [id, brief, isControlled, isServiceRunInPast]);

	const permanentlyDismissBrief = () => {
		setIsBriefOpen(false);
		setIsPermanentlyDismissed(true);
		AsyncStorage.setItem(`servicerun_${serviceRun.id}_brief_dismissed`, 'true');
	};

	const handleOnLinkPress = useCallback((_, href) => Linking.openURL(href), []);

	const showAcknowledgeButton = Boolean(requireAcknowledgement && !serviceRun.briefAcknowledgedAt);

	const acknowledgeBrief = useCallback(async () => {
		await acknowledgeBriefMutation();
		if (onAcknowledged) {
			onAcknowledged();
		}
		setIsBriefOpen(false);
	}, [acknowledgeBriefMutation, onAcknowledged, setIsBriefOpen]);

	return (
		<>
			{
				!hideButton && (
					<Button
						style={style}
						label="Brief"
						onPress={() => setIsBriefOpen(true)}
						disabled={Boolean(!brief)}
						secondary={secondary}
					/>
				)
			}
			<Dialog
				isOpen={isBriefOpen}
				onClose={() => setIsBriefOpen(false)}
			>
				<View>
					<Heading heading="Today's service run" />
					<View>
						<ScrollView
							scrollIndicatorInsets={{ right: 1, color: 'red' }}
							style={{ maxHeight: Dimensions.get('window').height - 300 }}
						>
							<HtmlRenderer
								baseStyle={{ flexWrap: 'wrap', color: theme.text1 }}
								html={serviceRun.brief}
								onLinkPress={handleOnLinkPress}
								tagsStyles={{
									p: { color: theme.text1 },
									span: { color: theme.text1 },
									div: { color: theme.text1 },
									h1: { color: theme.text1 },
									h2: { color: theme.text1 },
									h3: { color: theme.text1 },
									h4: { color: theme.text1 },
									h5: { color: theme.text1 },
									h6: { color: theme.text1 },
									li: { color: theme.text1 },
									a: { color: theme.linkColor1 },
								}}
							/>
						</ScrollView>
					</View>
					<View>
						{
							showAcknowledgeButton && (
								<Button
									label="I've read the brief"
									style={{ marginTop: 10 }}
									secondary={true}
									loading={acknowledging}
									disabled={acknowledging || disableActions}
									onPress={acknowledgeBrief}
								/>
							)
						}
						<Button
							label="Dismiss"
							style={{ marginTop: showAcknowledgeButton ? 5 : 10 }}
							onPress={() => setIsBriefOpen(false)}
						/>
						{
							!disablePermanentDismiss && !isPermanentlyDismissed && !isServiceRunInPast && !showAcknowledgeButton && (
								<Button
									label="Dismiss permanently"
									style={{ marginTop: 5 }}
									secondary={true}
									onPress={permanentlyDismissBrief}
								/>
							)
						}
					</View>
				</View>
			</Dialog>
		</>
	);
};

export default ServiceRunBrief;
